import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Menu from "@mui/material/Menu";
import { makeStyles } from "components/mui-core";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { grayColor } from "vendor/colors";
import { AppNotification, EmptyAppNotification } from "./AppNotification";
import { useAppNotifications } from "./AppNotificationProvider";
import { AppNotificationsButton } from "./AppNotificationsButton";
import { AppNotificationSummary } from "./AppNotificationSummary";
import { NOTIFICATIONS_TEXT, NOTIFICATIONS_TITLE } from "./utils";

const useStyles = makeStyles()({
  menu: {
    marginLeft: "70px",
    marginTop: "-40px",
  },
  notificationsButton: {
    position: "relative",
  },
  notificationsContainer: {
    bottom: "0px",
    backgroundColor: "#FFF",
    zIndex: 999999,
    borderRadius: "4px",
    boxShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.02);",
    transition: "height 2s ease-in-out",
    padding: "10px 0px 0px 0px",
    maxHeight: "calc(100vh - 100px)",
    overflowY: "auto",
  },
  overflow: {
    position: "sticky",
    bottom: "0px",
    width: "100%",
    height: "10px",
    backgroundColor: "rgba(0,0,0,0.15)",
    transition: "background-color 0.25s ease-in-out",
    maskImage: "linear-gradient(to bottom, transparent 0%, black 100%)",
    "&.hidden": {
      backgroundColor: "transparent",
    },
  },
  notificationsBorder: {
    "& > *:nth-child(even)": {
      borderTop: "1px solid #ddd",
      borderBottom: "1px solid #ddd",
    },
    "& > *:last-child": {
      borderBottom: "none",
    },
  },
  dismissedAlertsLabel: {
    marginTop: "20px",
    marginBottom: "10px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    color: grayColor[3],
    fontWeight: "bold",
    paddingLeft: "10px",
    fontSize: "12px",
    textTransform: "uppercase",
    justifyContent: "center",
    cursor: "pointer",
    transition: "textShadow 0.25s ease-in-out",
    "&:hover": {
      textShadow: "0px 0px 1px #ccc",
    },
  },
});

export const AppNotifications = ({
  onActive,
}: {
  onActive?: (active: boolean) => void;
}) => {
  const { classes: styles, cx } = useStyles();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    onActive?.(open);
  }, [open, onActive]);

  const { dismissedNotifications, visibleNotifications } =
    useAppNotifications();

  const [dismissedOpen, setDismissedOpen] = useState(
    !visibleNotifications.length
  );

  const onClickSummary = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (open) {
      const notificationsContainer = document.querySelector(
        "#notifications-container"
      );

      const onScroll = () => {
        const oi = notificationsContainer?.querySelector(".overflow-indicator");

        if (notificationsContainer && oi) {
          const TRIGGER_THRESHOLD = 20;
          const scroll = notificationsContainer.scrollTop;
          const height =
            notificationsContainer.scrollHeight -
            notificationsContainer.clientHeight;

          if (scroll > height - TRIGGER_THRESHOLD) {
            oi.classList.add("hidden");
          } else if (oi.classList.contains("hidden")) {
            oi.classList.remove("hidden");
          }
        }
      };

      notificationsContainer?.addEventListener("scroll", onScroll);

      return () =>
        notificationsContainer?.removeEventListener("scroll", onScroll);
    }
  }, [open]);

  return (
    <>
      <AppNotificationSummary onClick={onClickSummary} />

      <div className={styles.notificationsButton}>
        <AppNotificationsButton
          open={open}
          onOpen={() => setOpen((prev) => !prev)}
        />
      </div>

      <Menu
        PopoverClasses={{ paper: styles.menu }}
        open={open}
        onClose={() => setOpen(false)}>
        <div
          className={cx(styles.notificationsContainer)}
          id="notifications-container">
          <div className={styles.notificationsBorder}>
            {visibleNotifications.map((n: any) => (
              <AppNotification
                id={n.id}
                type="alert"
                title={NOTIFICATIONS_TITLE[n.reason]()}
                subtitle={NOTIFICATIONS_TEXT[n.reason]({
                  campaignId: n.campaignId,
                  campaignName: n.campaignName,
                  lineitemId: n.lineitemId,
                  lineitemName: n.lineitemName,
                  rejectedInstalls: n.rejectedInstalls,
                  budget: n.budget,
                })}
              />
            ))}

            {!visibleNotifications.length && <EmptyAppNotification />}
          </div>

          {!!dismissedNotifications.length && (
            <>
              <div
                className={styles.dismissedAlertsLabel}
                onClick={() => setDismissedOpen((prev) => !prev)}>
                {dismissedOpen ? <ExpandLess /> : <ExpandMore />}
                Dismissed alerts
                {dismissedOpen ? <ExpandLess /> : <ExpandMore />}
              </div>

              <AnimatePresence>
                {dismissedOpen && (
                  <motion.div
                    className={styles.notificationsBorder}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.3 },
                    }}
                    exit={{ opacity: 0, transition: { duration: 0.15 } }}>
                    {dismissedNotifications.map((n: any) => (
                      <AppNotification
                        id={n.id}
                        type="alert"
                        title={NOTIFICATIONS_TITLE[n.reason]()}
                        subtitle={NOTIFICATIONS_TEXT[n.reason]({
                          campaignId: n.campaignId,
                          campaignName: n.campaignName,
                          lineitemId: n.lineitemId,
                          lineitemName: n.lineitemName,
                          rejectedInstalls: n.rejectedInstalls,
                          budget: n.budget,
                        })}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

          <div
            className={cx(
              styles.overflow,
              "overflow-indicator",
              "hidden"
            )}></div>
        </div>
      </Menu>
    </>
  );
};
